import Bugsnag from '@bugsnag/js'
import { useRouter } from 'next/router'
import { useQuery } from 'react-query'

import { useDebounce } from '@cash-design-system/react'
import type { Region } from '@cash-web/shared-data-access-region-provider'
import { useCountryFromRegionProvider } from '@cash-web/shared-data-access-region-provider'
import {
  DEBOUNCED_TIME,
  MAX_DISPLAYED_RESULTS,
  MAX_DISPLAYED_RESULTS_SEARCH_PAGE,
  QUERY_MIN_LENGTH,
  toSearchArticleResults,
} from '@cash-web/support/feature-main'
import { defaultRegion, supportedRegions } from '@cash-web/support/utils-region-config'

import { getBestLocaleForRegion } from '../../utils/LocaleDetection'
import { searchArticles } from '../api'

interface SearchQueryProps {
  query: string
  perPage?: number
  locale?: { country_code: string; language_code: string }
  enabled?: boolean
  showMoreResults?: boolean
}

const useSearchQuery = ({ query, showMoreResults = false }: SearchQueryProps) => {
  const debouncedQuery = useDebounce(query.trim(), DEBOUNCED_TIME)
  const router = useRouter()
  const region = useCountryFromRegionProvider()?.toLowerCase() as Lowercase<Region> | undefined
  const supportedRegion = region && supportedRegions[region] ? region : defaultRegion

  const localeToRequest = getBestLocaleForRegion(supportedRegion, router.locale)

  return useQuery(
    ['search', debouncedQuery, supportedRegion, localeToRequest],
    ({ signal }) => {
      const payload = {
        search_text: debouncedQuery,
        per_page: showMoreResults ? MAX_DISPLAYED_RESULTS : MAX_DISPLAYED_RESULTS_SEARCH_PAGE,
        locale: { country_code: supportedRegion.toUpperCase() as Uppercase<Region>, language_code: localeToRequest },
        page: 1,
      }
      return searchArticles(payload, signal).then(toSearchArticleResults)
    },
    {
      retry: false,
      enabled: debouncedQuery?.length > QUERY_MIN_LENGTH,
      onError: error => {
        if (error instanceof Error) {
          Bugsnag.notify(error)
        }
      },
    }
  )
}

export default useSearchQuery
